export type MediaErrorKind = 'denied' | 'not_found' | 'in_use' | 'insecure' | 'unsupported' | 'unknown';

export function classifyMediaError(e: unknown): MediaErrorKind {
  if (typeof window !== 'undefined' && window.isSecureContext === false) return 'insecure';
  const name = e instanceof DOMException || e instanceof Error ? e.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'denied';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'not_found';
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'in_use';
    case 'TypeError':
      return 'unsupported';
    default:
      return 'unknown';
  }
}

export type BrowserKind = 'chrome' | 'safari' | 'firefox' | 'edge' | 'ios' | 'android' | 'other';

export function detectBrowser(ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : ''): BrowserKind {
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Edg\//.test(ua)) return 'edge';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Android/i.test(ua)) return 'android';
  if (/Chrome\//.test(ua)) return 'chrome';
  if (/Safari\//.test(ua)) return 'safari';
  return 'other';
}

export const PERMISSION_HELP: Record<BrowserKind, { name: string; steps: string[] }> = {
  chrome: {
    name: 'Chrome',
    steps: ['Click the camera icon (or the tune icon) at the left of the address bar.', 'Set Camera and Microphone to “Allow”.', 'Reload this page.'],
  },
  edge: {
    name: 'Edge',
    steps: ['Click the lock icon at the left of the address bar.', 'Choose “Permissions for this site” and allow Camera and Microphone.', 'Reload this page.'],
  },
  firefox: {
    name: 'Firefox',
    steps: ['Click the crossed-out camera icon in the address bar.', 'Remove the “Blocked” permission for camera and microphone.', 'Reload and choose “Allow” when asked.'],
  },
  safari: {
    name: 'Safari',
    steps: ['Open Safari ▸ Settings for This Website (or Safari ▸ Settings ▸ Websites).', 'Set Camera and Microphone to “Allow”.', 'Reload this page.'],
  },
  ios: {
    name: 'iPhone / iPad',
    steps: ['Tap “aA” in the address bar ▸ Website Settings.', 'Set Camera and Microphone to “Allow”.', 'If it’s still blocked: Settings ▸ Safari ▸ Camera / Microphone ▸ Allow.'],
  },
  android: {
    name: 'Android',
    steps: ['Tap the icon left of the address bar ▸ Permissions.', 'Allow Camera and Microphone.', 'If needed: Android Settings ▸ Apps ▸ your browser ▸ Permissions.'],
  },
  other: {
    name: 'your browser',
    steps: ['Open this site’s permissions from the address bar.', 'Allow Camera and Microphone.', 'Reload this page.'],
  },
};

export const MEDIA_ERROR_COPY: Record<MediaErrorKind, { title: string; body: string }> = {
  denied: { title: 'Camera and mic are blocked', body: 'Popin needs your camera and microphone for calls. You can change this in your browser settings.' },
  not_found: { title: 'No camera or mic found', body: 'Plug in or enable a camera and microphone, then try again.' },
  in_use: { title: 'Your camera is busy', body: 'Another app or tab is using your camera. Close it and try again.' },
  insecure: { title: 'This page isn’t secure', body: 'Browsers only allow camera access on secure (https) pages.' },
  unsupported: { title: 'Camera not supported here', body: 'This browser can’t share a camera. Try a recent Chrome, Safari, Firefox or Edge.' },
  unknown: { title: 'Couldn’t start your camera', body: 'Something stopped your camera from starting. Try again.' },
};

export async function getPreviewStream(): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new DOMException('getUserMedia not available', 'TypeError');
  }
  return navigator.mediaDevices.getUserMedia({
    video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
    audio: { echoCancellation: true, noiseSuppression: true },
  });
}

export function stopStream(s: MediaStream | null | undefined): void {
  s?.getTracks().forEach((t) => t.stop());
}

export function isProbablyMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent));
}
